import { memo, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import type { QuickChartData, MultipleLineChart, LineChart } from '@trinity/chat';
import { LegendCollapseToggleContext } from '@trinity/chat-components/components/Chat/context';
import { AnalysisTable } from '@trinity/chat-components/components/QuantAnalysis';
import { KPIWidget as KPIWidgetUI } from '@trinity/chat-components/components/QuantAnalysis/KPIWidget';
import { NivoBarChart } from '@trinity/chat-components/components/Widgets/Charts/Bar';
import { CompactChartLegend } from '@trinity/chat-components/components/Widgets/Charts/CompactLegend';
import { NivoLineChart } from '@trinity/chat-components/components/Widgets/Charts/Line';
import { NivoMultipleBarChart } from '@trinity/chat-components/components/Widgets/Charts/MultipleBar';
import { NivoPieChart } from '@trinity/chat-components/components/Widgets/Charts/Pie';
import { NivoStackedBarChart } from '@trinity/chat-components/components/Widgets/Charts/StackedBar';
import { ProgressBarWidget } from '@trinity/chat-components/components/Widgets/ProgressBarWidget';
import { SemiCircleProgressWidget } from '@trinity/chat-components/components/Widgets/SemiCircleProgressWidget';
import { DEFAULT_COLORS } from '@trinity/chat-components/lib/chart-colors';

/**
 * ChartVisualizationUI — pure presentation component for chart widgets.
 *
 * All data is received via props. Uses `colors` prop instead of the
 * app-local `useOptionsColors` hook, so it is reusable across packages.
 */

type Props = {
  /** The chart data shape (discriminated union by chart type) */
  chartData: QuickChartData;
  /** Array of hex colors to use for chart data series */
  colors?: string[];
};

export const ChartVisualizationUI = memo(function ChartVisualizationUI({
  chartData,
  colors = DEFAULT_COLORS,
}: Props): React.ReactElement | null {
  const [legendOpen, setLegendOpen] = useState(false);

  const legend = useMemo(() => {
    const legendData: { key: string; color: string; label?: string }[] = [];

    switch (chartData.type) {
      case 'bar':
      case 'stacked-bar': {
        const data = chartData.chart.data;
        for (let i = 0; i < data.length; i++) {
          legendData.push({ key: data[i].x, color: colors[i % colors.length] });
        }
        break;
      }
      case 'pie': {
        const data = chartData.chart.data;
        for (let i = 0; i < data.length; i++) {
          legendData.push({ key: data[i].label, color: colors[i % colors.length] });
        }
        break;
      }
      case 'multiple-clustered-bar':
      case 'multiple-stacked-bar': {
        const keysSet = new Set<string>();
        for (const row of chartData.chart.data) {
          for (const point of row.data) {
            keysSet.add(point.x);
          }
        }
        const keys = Array.from(keysSet);
        for (let i = 0; i < keys.length; i++) {
          legendData.push({ key: keys[i], color: colors[i % colors.length] });
        }
        break;
      }

      case 'multiple-line': {
        for (let i = 0; i < chartData.chart.data.length; i++) {
          const series = chartData.chart.data[i];
          // Use series.id as the stable unique key; pass label as the display text if available.
          legendData.push({
            key: series.id,
            label: series.label ?? series.id,
            color: colors[i % colors.length],
          });
        }
        break;
      }
      default:
        break;
    }

    return legendData;
  }, [chartData, colors]);

  // Estimate legend rows: ~4 items per row at typical widths, ~22px per row
  const legendRows = legend.length > 0 ? Math.ceil(legend.length / 4) : 0;
  const chartHeight = Math.max(180, 280 - legendRows * 24);

  return (
    <LegendCollapseToggleContext.Provider value={[legendOpen, setLegendOpen]}>
      <ChartContainer>
        <ChartContent style={{ height: chartHeight }}>{renderChart()}</ChartContent>
        {legend.length > 0 && <CompactChartLegend legend={legend} />}
      </ChartContainer>
    </LegendCollapseToggleContext.Provider>
  );

  function renderChart(): React.ReactElement | null {
    switch (chartData.type) {
      case 'bar':
        return (
          <NivoBarChart
            chart={chartData.chart}
            colors={colors}
            unit={chartData.options?.unit}
            isPrefix={chartData.options?.isPrefix}
          />
        );
      case 'stacked-bar':
        return (
          <NivoStackedBarChart
            chart={chartData.chart}
            colors={colors}
            unit={chartData.options?.unit}
            isPrefix={chartData.options?.isPrefix}
          />
        );
      case 'pie':
        return (
          <NivoPieChart
            chart={chartData.chart}
            colors={colors}
            unit={chartData.options?.unit}
            isPrefix={chartData.options?.isPrefix}
          />
        );
      case 'multiple-clustered-bar':
        return (
          <NivoMultipleBarChart
            chart={chartData.chart}
            colors={colors}
            isStacked={false}
            unit={chartData.options?.unit}
            isPrefix={chartData.options?.isPrefix}
            seriesToN={chartData.options?.seriesToN}
          />
        );
      case 'multiple-stacked-bar':
        return (
          <NivoMultipleBarChart
            chart={chartData.chart}
            colors={colors}
            isStacked={true}
            unit={chartData.options?.unit}
            isPrefix={chartData.options?.isPrefix}
            seriesToN={chartData.options?.seriesToN}
          />
        );

      case 'multiple-line':
        return <NivoLineChart chart={chartData.chart as LineChart | MultipleLineChart} colors={colors} />;
      case 'kpi-widget':
        return (
          <KPIWidgetUI
            title={chartData.title ?? ''}
            backgroundColor="transparent"
            values={chartData.values ?? []}
            labels={chartData.labels ?? []}
          />
        );
      case 'progress-bar-widget':
        return (
          <ProgressBarWidget
            title={chartData.title ?? ''}
            backgroundColor="transparent"
            values={chartData.values ?? []}
            minValues={chartData.minValues ?? []}
            maxValues={chartData.maxValues ?? []}
            labels={chartData.labels ?? []}
            colors={chartData.colors ?? colors}
          />
        );
      case 'semi-circle-progress-widget':
        return (
          <SemiCircleProgressWidget
            title={chartData.title ?? ''}
            backgroundColor="transparent"
            values={chartData.values ?? []}
            minValues={chartData.minValues ?? []}
            maxValues={chartData.maxValues ?? []}
            labels={chartData.labels ?? []}
            colors={chartData.colors ?? colors}
            units={chartData.units}
            insights={chartData.insights}
          />
        );
      case 'data-table': {
        const tableData = {
          columnNames: chartData.customColumnNames ?? chartData.data.columnNames,
          rowNames: chartData.customRowNames ?? chartData.data.rowNames,
          records: chartData.data.records,
          metadata: chartData.data.metadata,
        };
        return (
          <DataTableContainer>
            <AnalysisTable
              data={tableData}
              headerBackgroundColor={chartData.headerColor ?? colors[0]}
              initialSortConfig={chartData.sortConfig}
              disableSorting={false}
              disableSelection={true}
            />
          </DataTableContainer>
        );
      }
      default:
        return null;
    }
  }
});

const ChartContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  flex: 1,
  minHeight: 0,
});

const ChartContent = styled.div({
  minHeight: 0,
});

const DataTableContainer = styled.div({
  width: '100%',
  height: '100%',
  overflow: 'auto',
});
